import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { campaignAPI } from '../services/api';
import './QuestionBank.css';

const C = {
  brand: '#6366F1',
  brandHover: '#4F46E5',
  surface: '#FFFFFF',
  surfaceAlt: '#F3F4F6',
  border: '#E5E7EB',
  borderLight: '#F1F5F9',
  t1: '#111827',
  t3: '#6B7280',
  t4: '#9CA3AF',
  green: '#10B981',
  amber: '#F59E0B',
  red: '#EF4444',
};

const DIFF_COLOR = { easy: C.green, medium: C.amber, hard: C.red };

function Pill({ label, color }) {
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: '3px 8px',
        borderRadius: 6,
        background: `${color}1a`,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

function BtnPrimary({ children, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        fontSize: 12,
        background: disabled ? C.t4 : C.brand,
        color: 'white',
        border: 'none',
        padding: '8px 16px',
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontWeight: 600,
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

function difficultySummary(byDifficulty) {
  const order = ['easy', 'medium', 'hard'];
  return order
    .filter((d) => byDifficulty[d])
    .map((d) => `${byDifficulty[d]} ${d}`)
    .join(', ');
}

const SOURCE_LABEL = {
  wizard_preview: 'Wizard preview',
  wizard_regenerate: 'Wizard regenerate',
  wizard_final: 'Position',
  live_interview: 'Live interview',
  recruiter_authored: 'Recruiter authored',
  recruiter_edit: 'Recruiter edit',
};

function UploadQuestionnaireModal({ positions, defaultRole, onClose }) {
  const [jobRole, setJobRole] = useState(defaultRole || '');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setError('');
    setNotice('');
    setFile(f);
  };

  const handleUpload = () => {
    if (!jobRole.trim()) { setError('Please choose or type a position.'); return; }
    if (!file) { setError('Please choose a PDF or Word document.'); return; }
    setError('');
    // Backend processing (document parsing + question extraction) isn't wired up yet.
    setNotice("Upload received — document parsing isn't connected yet. This will extract and add the questions to the bank once the backend is ready.");
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 14, width: 480, maxWidth: '92vw', maxHeight: '86vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.t1 }}>Upload Questionnaire</div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: C.t3, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          <p style={{ fontSize: 12, color: C.t3, lineHeight: 1.6, margin: '0 0 16px' }}>
            Upload a PDF or Word document with your own interview questions — we'll read it and append the extracted questions to the bank for the position you choose below.
          </p>

          {error && (
            <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 12, color: '#991B1B' }}>
              {error}
            </div>
          )}
          {notice && (
            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 12, color: '#1E40AF' }}>
              {notice}
            </div>
          )}

          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: C.t3, marginBottom: 5 }}>Position</label>
          <input
            type="text"
            list="qb-upload-role-options"
            value={jobRole}
            onChange={(e) => { setJobRole(e.target.value); setNotice(''); }}
            placeholder="e.g. Senior Software Engineer"
            style={{ width: '100%', fontSize: 13, padding: '9px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontFamily: 'inherit', color: C.t1, boxSizing: 'border-box', marginBottom: 16 }}
          />
          <datalist id="qb-upload-role-options">
            {positions.map((p) => <option key={p.job_role} value={p.job_role} />)}
          </datalist>

          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: C.t3, marginBottom: 5 }}>Document</label>
          <label
            htmlFor="qb-upload-file-input"
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
              border: `1.5px dashed ${C.border}`, borderRadius: 10, padding: '22px 16px', cursor: 'pointer', textAlign: 'center',
              background: C.surfaceAlt,
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke={C.t3} strokeWidth="1.6" width="22" height="22">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>
              {file ? file.name : 'Click to choose a PDF or Word file'}
            </span>
            <span style={{ fontSize: 11, color: C.t4 }}>.pdf, .doc, .docx</span>
          </label>
          <input
            id="qb-upload-file-input"
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </div>

        <div style={{ padding: '14px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose}
            style={{ fontSize: 13, color: C.t3, background: 'none', border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
            Close
          </button>
          <button type="button" onClick={handleUpload}
            style={{ fontSize: 13, fontWeight: 700, color: 'white', background: C.brand, border: 'none', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>
            Upload & Extract Questions
          </button>
        </div>
      </div>
    </div>
  );
}

export default function QuestionBank() {
  const [view, setView] = useState('positions'); // 'positions' | 'all'
  const [positions, setPositions] = useState([]);
  const [allQuestions, setAllQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [dedupBusy, setDedupBusy] = useState(false);
  const [dedupMsg, setDedupMsg] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  const loadPositions = useCallback(async () => {
    try {
      const res = await campaignAPI.getQuestionBankByPosition();
      setPositions(res.data?.positions || []);
    } catch (err) {
      console.error('Error loading question bank positions:', err);
      setPositions([]);
    }
  }, []);

  const loadAllQuestions = useCallback(async (roleFilter) => {
    try {
      const res = await campaignAPI.getQuestionBank(roleFilter);
      setAllQuestions(res.data?.questions || []);
    } catch (err) {
      console.error('Error loading question bank:', err);
      setAllQuestions([]);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadPositions(), loadAllQuestions()]);
    setLoading(false);
  }, [loadPositions, loadAllQuestions]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const totalQuestions = allQuestions.length;
  const totalPositions = positions.length;

  const filteredPositions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return positions;
    return positions.filter((p) => p.job_role.toLowerCase().includes(q));
  }, [positions, search]);

  const filteredAllQuestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = allQuestions;
    if (selectedRole) rows = rows.filter((r) => r.job_role === selectedRole);
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.job_role || '').toLowerCase().includes(q) ||
        (r.question_text || '').toLowerCase().includes(q)
    );
  }, [allQuestions, search, selectedRole]);

  const openPosition = (jobRole) => {
    setSelectedRole(jobRole);
    setView('all');
  };

  const handleDeduplicate = async (roleScope) => {
    setDedupBusy(true);
    setDedupMsg('');
    try {
      const res = await campaignAPI.deduplicateQuestionBank(roleScope);
      const { removed, groups_scanned } = res.data || {};
      setDedupMsg(
        removed > 0
          ? `Removed ${removed} duplicate question${removed === 1 ? '' : 's'} across ${groups_scanned} position${groups_scanned === 1 ? '' : 's'}.`
          : 'No duplicates found.'
      );
      await loadAll();
    } catch (err) {
      setDedupMsg(err.response?.data?.detail || 'Deduplication failed. Please try again.');
    } finally {
      setDedupBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="qb-page">
        <div className="qb-loading">
          <div className="qb-spinner" />
          <p>Loading question bank...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="qb-page">
      <main style={{ padding: '28px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: C.t1, marginBottom: 3 }}>Question Bank</h2>
            <div style={{ fontSize: 12, color: C.t3 }}>
              {totalQuestions} question{totalQuestions === 1 ? '' : 's'} across {totalPositions} position
              {totalPositions === 1 ? '' : 's'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              style={{
                fontSize: 12, fontWeight: 600, color: C.t1, background: C.surface,
                border: `1px solid ${C.border}`, padding: '8px 16px', borderRadius: 8,
                cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke={C.t1} strokeWidth="1.8" width="13" height="13">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              Upload Questionnaire
            </button>
            <BtnPrimary onClick={() => handleDeduplicate(selectedRole)} disabled={dedupBusy}>
              {dedupBusy ? 'Scanning…' : selectedRole ? `Deduplicate "${selectedRole}"` : 'Deduplicate All'}
            </BtnPrimary>
          </div>
        </div>

        {showUploadModal && (
          <UploadQuestionnaireModal
            positions={positions}
            defaultRole={selectedRole}
            onClose={() => setShowUploadModal(false)}
          />
        )}

        {dedupMsg && (
          <div
            style={{
              fontSize: 12,
              color: C.t1,
              background: C.surfaceAlt,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: '8px 12px',
              marginBottom: 16,
            }}
          >
            {dedupMsg}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: 1,
              background: C.surfaceAlt,
              padding: 3,
              borderRadius: 8,
              border: `1px solid ${C.border}`,
              width: 'fit-content',
            }}
          >
            {[
              ['positions', 'Browse by Position'],
              ['all', 'All Questions'],
            ].map(([v, l]) => (
              <button
                key={v}
                type="button"
                onClick={() => {
                  setView(v);
                  if (v === 'positions') setSelectedRole(null);
                }}
                style={{
                  padding: '6px 16px',
                  borderRadius: 6,
                  border: 'none',
                  background: view === v ? C.surface : 'transparent',
                  color: view === v ? C.t1 : C.t3,
                  fontSize: 12,
                  fontWeight: view === v ? 600 : 400,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  boxShadow: view === v ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {l}
              </button>
            ))}
          </div>
          <div style={{ flex: '1 1 220px', minWidth: 200, maxWidth: 360 }}>
            <input
              type="search"
              placeholder="Search by role or question text…"
              aria-label="Search question bank"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
              style={{
                width: '100%',
                fontSize: 13,
                padding: '8px 12px',
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                fontFamily: 'inherit',
                color: C.t1,
                background: C.surface,
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {selectedRole && view === 'all' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 12, color: C.t3 }}>Filtered to position:</span>
            <Pill label={selectedRole} color={C.brand} />
            <button
              type="button"
              onClick={() => setSelectedRole(null)}
              style={{
                fontSize: 11,
                color: C.brand,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 500,
                fontFamily: 'inherit',
              }}
            >
              Clear
            </button>
          </div>
        )}

        {view === 'positions' ? (
          filteredPositions.length === 0 ? (
            <div className="qb-empty">
              <p>No positions in the bank yet. Generate questions in a position to start building it.</p>
            </div>
          ) : (
            <div className="qb-positions-grid">
              {filteredPositions.map((p) => (
                <div
                  key={p.job_role}
                  style={{
                    background: C.surface,
                    borderRadius: 10,
                    border: `1px solid ${C.border}`,
                    padding: 16,
                    transition: 'all .18s',
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.t1, marginBottom: 4 }}>{p.job_role}</div>
                  <div style={{ fontSize: 12, color: C.t4, marginBottom: 14 }}>
                    {p.total} question{p.total === 1 ? '' : 's'}
                    {difficultySummary(p.by_difficulty) ? ` · ${difficultySummary(p.by_difficulty)}` : ''}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => openPosition(p.job_role)}
                      style={{
                        fontSize: 12,
                        background: C.surfaceAlt,
                        color: C.t1,
                        border: `1px solid ${C.border}`,
                        padding: '7px 14px',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontFamily: 'inherit',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      Open position
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : filteredAllQuestions.length === 0 ? (
          <div className="qb-empty">
            <p>No questions match this view.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredAllQuestions.map((q) => (
              <div
                key={q.id}
                style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: '14px 16px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 8 }}>
                  <div style={{ fontSize: 13, color: C.t1, lineHeight: 1.5, flex: 1 }}>{q.question_text}</div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <Pill label={(q.difficulty || 'medium')} color={DIFF_COLOR[q.difficulty] || C.t4} />
                    {q.is_approved && <Pill label="In use" color={C.brand} />}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, fontSize: 11, color: C.t4, flexWrap: 'wrap' }}>
                  <span>{q.job_role}</span>
                  <span style={{ color: C.t5 || C.t4 }}>·</span>
                  <span>{q.question_type || 'technical'}</span>
                  <span style={{ color: C.t5 || C.t4 }}>·</span>
                  <span>{SOURCE_LABEL[q.source] || q.source || 'unknown source'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
